import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { maskCEP, unmaskCEP } from "@/lib/utils";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { CriarUsuarioFormData } from "../schemas/usuario.schema";

const UFS_BRASIL = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

interface EnderecoProps {
  form: UseFormReturn<CriarUsuarioFormData>;
  enderecos: CriarUsuarioFormData["enderecos"];
  adicionarEndereco: () => void;
  removerEndereco: (index: number) => void;
  atualizarEndereco: (index: number, campo: string, valor: string | boolean) => void;
  atualizarEnderecoCompleto: (index: number, campos: Partial<CriarUsuarioFormData["enderecos"][0]>) => void;
  readOnly?: boolean;
}

export const Endereco = ({ form, enderecos, adicionarEndereco, removerEndereco, atualizarEndereco, atualizarEnderecoCompleto, readOnly = false }: EnderecoProps) => {
  const { toast } = useToast();
  // Refs para armazenar os timeouts de debounce por índice de endereço
  const debounceTimeouts = useRef<Record<number, NodeJS.Timeout>>({});

  const buscarCep = async (cep: string, index: number) => {
    try {
      // Remove formatação do CEP
      const cepLimpo = cep.replace(/\D/g, '');
      
      // Verifica se o CEP tem 8 dígitos
      if (cepLimpo.length !== 8) {
        return;
      }

      const response: { result: any } = await api.get(`/api/cep/${cepLimpo}`);
      
      if (response.result && !response.result.erro) {
        // Preenche os campos do endereço com os dados retornados
        const dadosCep = response.result;
        
        // Atualiza todos os campos de uma vez usando função de atualização para garantir estado atualizado
        atualizarEnderecoCompleto(index, {
          logradouro: dadosCep.logradouro || '',
          bairro: dadosCep.bairro || '',
          cidade: dadosCep.localidade || '',
          uf: dadosCep.uf || '',
          complemento: dadosCep.complemento || ''
        });
        
        toast({
          title: "CEP encontrado",
          description: "Endereço preenchido automaticamente",
        });
      } else {
        toast({
          title: "CEP não encontrado",
          description: "Verifique se o CEP está correto",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Erro ao buscar CEP:", error);
      toast({
        title: "Erro",
        description: error?.message || "Erro ao buscar o CEP",
        variant: "destructive",
      });
    }
  };

  const handleCepChange = (index: number, value: string) => {
    // Atualiza o valor do CEP sem máscara
    atualizarEndereco(index, "cep", unmaskCEP(value));
    
    // Remove o timeout anterior se existir
    if (debounceTimeouts.current[index]) {
      clearTimeout(debounceTimeouts.current[index]);
    }
    
    // Remove formatação para verificar se tem 8 dígitos
    const cepLimpo = unmaskCEP(value);
    
    // Se tiver 8 dígitos, aguarda 500ms sem digitar para fazer a busca
    if (cepLimpo.length === 8) {
      debounceTimeouts.current[index] = setTimeout(() => {
        buscarCep(cepLimpo, index);
      }, 500);
    }
  };

  // Limpa os timeouts ao desmontar o componente
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Endereços</CardTitle>
          {!readOnly && (
            <Button type="button" onClick={adicionarEndereco} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Endereço
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {enderecos.map((endereco, index) => (
          <Card key={index} className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">Endereço {index + 1}</h4>
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removerEndereco(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name={`enderecos.${index}.cep`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CEP</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={maskCEP(field.value || '')}
                        onChange={(e) => {
                          field.onChange(unmaskCEP(e.target.value));
                          handleCepChange(index, e.target.value);
                        }}
                        placeholder="00000-000"
                        maxLength={9}
                        disabled={readOnly}
                        readOnly={readOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name={`enderecos.${index}.numero`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        placeholder="123"
                        disabled={readOnly}
                        readOnly={readOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name={`enderecos.${index}.logradouro`}
              render={({ field }) => (
                <FormItem className="mt-4">
                  <FormLabel>
                    Logradouro <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value || ''}
                      placeholder="Rua, Avenida, etc."
                      disabled={readOnly}
                      readOnly={readOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <FormField
                control={form.control}
                name={`enderecos.${index}.bairro`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bairro</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        placeholder="Nome do bairro"
                        disabled={readOnly}
                        readOnly={readOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name={`enderecos.${index}.cidade`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cidade</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        placeholder="Nome da cidade"
                        disabled={readOnly}
                        readOnly={readOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <FormField
                control={form.control}
                name={`enderecos.${index}.uf`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>UF</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ""} disabled={readOnly}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a UF" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {UFS_BRASIL.map((uf) => (
                          <SelectItem key={uf} value={uf}>
                            {uf}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name={`enderecos.${index}.complemento`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Complemento</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        placeholder="Apto, Bloco, etc."
                        disabled={readOnly}
                        readOnly={readOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name={`enderecos.${index}.principal`}
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0 mt-4">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value || false}
                      onChange={field.onChange}
                      className="rounded"
                      disabled={readOnly}
                    />
                  </FormControl>
                  <FormLabel className="!mt-0">Endereço principal</FormLabel>
                </FormItem>
              )}
            />
          </Card>
        ))}
        
        {enderecos.length === 0 && (
          <div className="text-center text-muted-foreground py-8">
            Nenhum endereço cadastrado. Clique em "Adicionar Endereço" para começar.
          </div>
        )}
      </CardContent>
    </Card>
  );
};
